import React, { useRef, useState, useEffect } from "react";
import { BUYER_QR_BASE_URL } from "../../../config/api-config";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  FaCar,
  FaMotorcycle,
  FaShip,
  FaTrailer,
  FaTruck,
} from "react-icons/fa";
import { MdElectricScooter } from "react-icons/md";
import ApiCall from "../../../Apicall/ApiCall";
import QRCode from "react-qr-code";
import CryptoJS from "crypto-js";
import "react-toastify/dist/ReactToastify.css";
import { dsToast } from "../../utils/dsToast";
import { useSignalR } from "../../../context/SignalRContext.js";
import { useNavigate } from "react-router-dom";
import BankIdSignModal from "../../../components/BankIdSignModal";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import SellerFlowTrack from "./SellerFlowTrack";
import { BackArrow, ForwardArrow } from "./StatusMark";
import { escapeHtml } from "./dealDialogs";
import { announceDealCancelled } from "../../lib/dealSession";
import { guardedNavigate } from "./DealSessionManager";
import { saveDealStep } from "../../lib/dealStep";
import { normalizeRegistration } from "../../lib/registrationNo";
import { BANKID_MOCK_SIGN, mockSignRefPrepared } from "../../lib/mockBankId";
import { logDealEvent } from "../../../utils/dealLog";
import RegistrationPlate from "../common/RegistrationPlate";
import ContractMasthead from "./ContractMasthead";
import { ContractSourceButton, ContractOptionRow, ContractSignatureClause, ContractOdometerAttestation, ContractVehicleSourceNote, ContractEditableVehicleNumber } from "./ContractReviewUi";
import { Clause, Register, RegisterRow, Terms, Party } from "./ContractDocumentParts";
import VehicleTypeVisual from "../vehicles/VehicleTypeVisual";
import { createContractUiMessage, FIELD_LOCK_MSG } from "./contractUiRealtime";
import { saveContractDetails } from "../../lib/contractDetails";
import { buildCarLinkPayload } from "../../lib/sellerContractPayload";
import { PRICE_MSG } from "./useSellerPriceLock";
import { formatRegNumber, formatSekAmount } from "../../lib/format.jsx";
import { useBankIdVerification } from "../auth/BankIdVerification";

// Reference (Klargo-Reference/pages/Index.tsx) ships fixed illustrations for
// car/motorcycle/moped only; other backend-configured vehicle types fall back
// to a plain icon badge so newly added types stay fully selectable.
export function visualKindFor(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("car")) return "car";
  if (n.includes("motorcycle")) return "motorcycle";
  if (n.includes("moped") || n.includes("scooter")) return "moped";
  if (n.includes("truck")) return "lightTruck";
  if (n.includes("camper") || n.includes("trailer") || n.includes("boat")) return "camper";
  return null;
}

// Backend vehicle type names are stored lowercase in English ("car", "motorcycle",
// "scooter"). Translate them for display only - never write the label back to form
// values, the raw name is what the API expects. Unknown types fall back to the raw
// name with a capital first letter.
export function vehicleLabelFor(name, t) {
  const kind = visualKindFor(name);
  if (kind) return t(`seller.vehicleTypeLabels.${kind}`);
  const raw = String(name || "").trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function vehicleIconFor(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("car")) return <FaCar color="#0a5c41" size={22} />;
  if (n.includes("motorcycle")) return <FaMotorcycle color="#0a5c41" size={22} />;
  if (n.includes("truck")) return <FaTruck color="#0a5c41" size={22} />;
  if (n.includes("boat")) return <FaShip color="#0a5c41" size={22} />;
  if (n.includes("camper") || n.includes("trailer")) return <FaTrailer color="#0a5c41" size={22} />;
  if (n.includes("moped") || n.includes("scooter")) return <MdElectricScooter color="#0a5c41" size={22} />;
  return <FaCar color="#0a5c41" size={22} />;
}

// Mirrors the mockup's dealFlowStore config/isUnusuallyLowPrice — a fixed,
// client-side reference value (there's no real market-valuation API here),
// used only to warn the seller, never to block the sale.
const PRICE_WARNING_CONFIG = { estimatedValue: 215000, absoluteThreshold: 5000, percentageThreshold: 0.1 };
const isUnusuallyLowPrice = (priceSek) => {
  if (!Number.isFinite(priceSek) || priceSek <= 0) return false;
  return (
    priceSek < PRICE_WARNING_CONFIG.estimatedValue * PRICE_WARNING_CONFIG.percentageThreshold ||
    priceSek < PRICE_WARNING_CONFIG.absoluteThreshold
  );
};
const formatKr = (n) => new Intl.NumberFormat("en-US").format(n).replace(/,/g, " ");


// Shared SweetAlert2 theme: mounts outside `.klargo-public` (see integration.css),
// so every dialog reuses this instead of the library's default look.
const SWAL_THEME = {
  buttonsStyling: false,
  customClass: {
    popup: "ds-swal-popup",
    icon: "ds-swal-icon",
    title: "ds-swal-title",
    htmlContainer: "ds-swal-text",
    confirmButton: "ds-swal-confirm",
  },
};

// The contract only carries the buyer's user id on this backend (no buyerName /
// buyerUserName fields come back), so when those are empty the name is read from
// the existing GetUserProfileById endpoint — no backend change.
export async function resolveBuyerName(contract) {
  const direct = contract?.buyerName || contract?.buyerUserName;
  if (direct) return direct;
  if (!contract?.buyerUserId) return "";
  try {
    const res = await ApiCall({
      url: "/User/GetUserProfileById",
      method: "GET",
      params: { Id: contract.buyerUserId },
    });
    const p = res?.data?.result || res?.result;
    const full = [p?.name, p?.surname].filter(Boolean).join(" ").trim();
    return full || p?.userName || "";
  } catch (err) {
    console.error("Error resolving buyer name:", err);
    return "";
  }
}

export async function getUserProfile(userId) {
  if (!userId) return null;
  try {
    const response = await ApiCall({
      url: "/User/GetUserProfileById",
      method: "GET",
      params: { Id: userId },
    });
    return response?.data?.result || response?.result || null;
  } catch (error) {
    console.error("Error resolving contract party profile:", error);
    return null;
  }
}

function SellerProfile() {
  const navigate = useNavigate();
  const { ensureVerified, verificationDialogs } = useBankIdVerification();
  const { t } = useTranslation();
  const { connectToDeal, messages, connection, sendDealMessage,leaveDealGroup } = useSignalR();
  // -------------------Managing States-------------------------------
  const [value, setValue] = useState("none");          // loan/lien option
  // Contract terms as last saved on the server ("none" when unset). The terms are part of the
  // signed contract, so they are saved as the seller picks them and before BankID starts —
  // never after signing (that would change the contract the seller just signed).
  const persistedTermsRef = useRef({ debt: "none", lien: "none" });
  const [debtOption, setDebtOption] = useState("none"); // debts option
  const [activeStep, setActiveStep] = useState(0);
  const [vehicleData, setVehicleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState("");
  const [priceValue, setPriceValue] = useState("");
  const [contractPriceEditing, setContractPriceEditing] = useState(false);
  const [contractPriceDraft, setContractPriceDraft] = useState("");
  const [contractPriceError, setContractPriceError] = useState("");
  const [contractPriceSaving, setContractPriceSaving] = useState(false);
  const [regError, setRegError] = useState("");
  const [priceError, setPriceError] = useState("");
  const regInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const advancingToReviewRef = useRef(false);
  // How many "Buyer connected to Seller" messages have already been acted on. The
  // SignalR message list only ever grows (and is replaced, not mutated, on each new
  // message), so clearing it in place is not enough to forget a buyer who was just
  // disconnected: the seller's own "disconnected" echo swaps in a fresh array that
  // still holds the old connect message and re-opened the "buyer connected" stage.
  // Counting consumed connect messages is immune to that.
  const consumedConnectMsgsRef = useRef(0);
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  const countConnectMsgs = (list) =>
    list.filter((m) => m.user === "buyer" && m.message === "Buyer connected to Seller").length;
  const forgetConnectedBuyer = () => {
    consumedConnectMsgsRef.current = countConnectMsgs(messagesRef.current);
  };
  const [sellerDisplayName, setSellerDisplayName] = useState("");
  const [sellerProfile, setSellerProfile] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [userId, setUserId] = useState(null);
  const [storedId, setStoredId] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [lastId, setLastId] = useState(null);
  const [finalContract, setFinalContract] = useState(null);
  const [odometerCertified, setOdometerCertified] = useState(false);
  // When the odometer was certified, as saved on the contract; undefined until this contract's saved
  // value has been read. odometerEpoch restarts the certification box when the reading is changed.
  const [odometerCertifiedAt, setOdometerCertifiedAt] = useState(undefined);
  const [odometerEpoch, setOdometerEpoch] = useState(0);
  // Matches the mock: "Not yet signed" is the hard-coded default for both
  // parties on every load — signing state is never read from the contract
  // record. It only ever changes within this session: this party's own
  // BankID success sets its own moment directly; the other party's arrives
  // solely via the "(Seller|Buyer) signed" SignalR notice.
  const [sellerSignedAt, setSellerSignedAt] = useState(null);
  const [buyerSignedAt, setBuyerSignedAt] = useState(null);
  const buyerSignedHandledRef = useRef(false);
  const bothSignedNavigateRef = useRef(false);
  // "Equipment included" starts pre-filled with the standard text until the seller edits it; what
  // they enter is saved on the contract when they leave the field, and restored when they come back.
  const [equipmentText, setEquipmentText] = useState(() => t("agreement.equipmentStandardText"));
  const [equipmentSaved, setEquipmentSaved] = useState(false);
  // The texts as last saved on the contract, so only a real change is saved and announced to the buyer.
  const savedTextsRef = useRef({ equipment: null, knownDefects: "" });
  const equipmentSavedTimerRef = useRef(null);
  const notifyBuyerContractUi = async (field, nextValue) => {
    if (!connection) return;
    try {
      await sendDealMessage("seller", createContractUiMessage(field, nextValue));
    } catch (error) {
      console.error("Failed to update the buyer contract UI:", error);
    }
  };
  const handleEquipmentBlur = () => {
    setEquipmentSaved(true);
    clearTimeout(equipmentSavedTimerRef.current);
    equipmentSavedTimerRef.current = setTimeout(() => setEquipmentSaved(false), 1600);
    if (equipmentText === (savedTextsRef.current.equipment ?? t("agreement.equipmentStandardText"))) return;
    savedTextsRef.current.equipment = equipmentText;
    void (async () => {
      await saveContractDetails(finalContract?.id, { equipmentIncluded: equipmentText });
      await notifyBuyerContractUi("equipment", equipmentText);
    })();
  };
  const [knownDefectsText, setKnownDefectsText] = useState("");
  const [knownDefectsSaved, setKnownDefectsSaved] = useState(false);
  const knownDefectsSavedTimerRef = useRef(null);
  const handleKnownDefectsBlur = () => {
    setKnownDefectsSaved(true);
    clearTimeout(knownDefectsSavedTimerRef.current);
    knownDefectsSavedTimerRef.current = setTimeout(() => setKnownDefectsSaved(false), 1600);
    if (knownDefectsText === savedTextsRef.current.knownDefects) return;
    savedTextsRef.current.knownDefects = knownDefectsText;
    void (async () => {
      await saveContractDetails(finalContract?.id, { knownDefects: knownDefectsText });
      await notifyBuyerContractUi("knownDefects", knownDefectsText);
    })();
  };
  // Read what is saved on the contract once per contract: the seller coming back (idle logout,
  // re-login) finds the texts and the certification as they left them. Later reloads of the
  // contract (after a price change) must not overwrite what is being typed.
  const hydratedContractRef = useRef(null);
  useEffect(() => {
    const c = finalContract;
    if (!c?.id) {
      hydratedContractRef.current = null;
      setOdometerCertified(false);
      setOdometerCertifiedAt(undefined);
      return;
    }
    if (hydratedContractRef.current === c.id) return;
    hydratedContractRef.current = c.id;
    setOdometerCertified(Boolean(c.odometerCertifiedAt));
    setOdometerCertifiedAt(c.odometerCertifiedAt || null);
    savedTextsRef.current = { equipment: c.equipmentIncluded ?? null, knownDefects: c.knownDefects ?? "" };
    setEquipmentText(c.equipmentIncluded ?? t("agreement.equipmentStandardText"));
    setKnownDefectsText(c.knownDefects ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalContract?.id]);
  const [QR, setQR] = useState("");
  const [buyerConnected, setBuyerConnected] = useState(false);
  const [sellerStartedDeal, setSellerStartedDeal] = useState(false);
  const [buyerToastShown, setBuyerToastShown] = useState(false);
  const [sellerSignModalOpen, setSellerSignModalOpen] = useState(false);
  const [refreshFinalContract, setRefreshFinalContract] = useState(false);
  const [buyerUserName, setBuyerUserName] = useState("");
  const [carRegNo, setCarRegNo] = useState("");
  const [vehicleTypeName, setVehicleTypeName] = useState("");
  // Remember the seller's current step on the contract (resume point after re-login).
  useEffect(() => {
    if (contractData?.id) saveDealStep(contractData.id, activeStep);
  }, [activeStep, contractData?.id]);

  // Vehicle type picked in step 0. No contract exists yet at that point — it is
  // created only after the registry (Bilvision) lookup succeeds in handleSearch.
  const [pendingVehicleType, setPendingVehicleType] = useState(null);

  // ------------------- Getting Current Seller ID from Local Storage -------------------------------
  useEffect(() => {
    const id = localStorage.getItem("currentContractID");
    if (id) {
      setStoredId(parseInt(id));
    }
  }, []);

  useEffect(() => {
    if (activeStep === 3) {
      const fetchAndEncryptData = async () => {
        try {
          const response = await ApiCall({
            url: "/ContractMain/GetContractMainById",
            method: "GET",
            params: { Id: contractData.id },
          });

          const contractData3 = response.result || response.data?.result;

          if (!contractData3) {
            Swal.fire(
              "❌ Error",
              "Failed to fetch latest contract data!",
              "error",
            );
            return;
          }

          // Try registration from contract, fall back to fetching from CarInfo
          if (contractData3.carInfoRegistrationNo) {
            setCarRegNo(contractData3.carInfoRegistrationNo);
          } else if (contractData3.carInfoId) {
            try {
              const carRes = await ApiCall({
                url: "/CarInfo/GetCarInfoById",
                method: "GET",
                params: { Id: contractData3.carInfoId },
              });
              const carInfo = carRes?.result || carRes?.data?.result;
              setCarRegNo(carInfo?.registrationNo || "");
            } catch (_) {
              setCarRegNo("");
            }
          }

          const currentTime = new Date().toISOString();
          const minimalData = {
            contractId: contractData3.id,
            sellerUserId: contractData3.sellerUserId,
            time: currentTime,
          };

          const secretKey = "Klargo3613";
          const encrypted = CryptoJS.AES.encrypt(
            JSON.stringify(minimalData),
            secretKey,
          ).toString();

          const qrUrl = `${BUYER_QR_BASE_URL}/${encodeURIComponent(
            encrypted,
          )}`;

          setQR(qrUrl);
        } catch (error) {
          console.error("❌ Error fetching contract data:", error);
          Swal.fire(
            "❌ Error",
            error.response?.data?.error?.message || "Unexpected server error.",
            "error",
          );
        }
      };

      fetchAndEncryptData();
    }
  }, [activeStep]);

  // -------------------Validation Schemas-------------------------------

  const validationSchemas = [
    Yup.object({
      vahicleTypeOptionId: Yup.number()
        .nullable()
        .required("Please select a vehicle type"),
    }),
  ];

  // ------------------- Api Calls-------------------------------
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const response = await ApiCall({
          url: "/Session/GetCurrentLoginInformations", // relative URL
          method: "GET",
        });
        const id = response.data?.result?.user?.id;
        setUserId(id);

        if (id) {
          try {
            const profRes = await ApiCall({
              url: "/User/GetUserProfileById",
              method: "GET",
              params: { Id: id },
            });
            const u = profRes?.data?.result || profRes?.result || {};
            setSellerProfile(u);
            setSellerDisplayName(u.fullName || [u.name, u.surname].filter(Boolean).join(" ") || u.userName || "");
          } catch (profErr) {
            console.error("Error fetching seller profile:", profErr);
          }
        }
      } catch (err) {
        console.error("Error fetching user ID:", err);
      }
    };

    fetchUserId();
  }, []);

  useEffect(() => {
    const fetchVehicleOptions = async () => {
      try {
        setLoading(true);
        const response = await ApiCall({
          url: "/VahicleTypeOption/GetAll", // relative to baseURL
          method: "GET",
        });

        if (response?.data?.result?.items) {
          // Klargo currently supports only car, motorcycle and moped/scooter.
          // Hide any other vehicle types (boat, trailer, truck, camper, …) even
          // if the backend still returns them.
          const ALLOWED_VEHICLE_TYPES = ["car", "motorcycle", "scooter", "moped", "mc"];
          setVehicleData(
            response.data.result.items.filter((v) =>
              ALLOWED_VEHICLE_TYPES.includes(
                String(v?.vahicleTypeName || "").trim().toLowerCase()
              )
            )
          );
        } else {
          setVehicleData([]);
        }
      } catch (err) {
        setError(err.message || "API call failed");
      } finally {
        setLoading(false);
      }
    };

    if (activeStep === 0) {
      fetchVehicleOptions();
    }
  }, [activeStep]);

  useEffect(() => {
    const fetchContract = async () => {
      if (!storedId) return;

      try {
        const response = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: storedId },
        });

        const data = response.data?.result;

        // If contract is expired or cancelled, clear stale localStorage and go to dashboard
        const expiryDate = new Date(data?.dealExpiryTime);
        // A deal lives exactly 60 minutes from creation (matches the backend).
        expiryDate.setMinutes(expiryDate.getMinutes() + 60);
        if (!data || data.dealCancel || expiryDate < new Date()) {
          localStorage.removeItem("currentContractID");
          navigate("/user/ChooseAction", { replace: true });
          return;
        }

        setContractData(data);

        // Backfill the local display state the deal-summary card reads —
        // otherwise vehicle type / price only show up when this component
        // just walked through steps 0-2 itself, not on a resumed/reloaded page.
        if (data.vahicleTypeOptionName) setVehicleTypeName(data.vahicleTypeOptionName);
        if (data.carValuationBySeller) setPriceValue(String(data.carValuationBySeller));

        // Resume to the right step if contract already has progress
        if (data.carInfoId && data.sellerDealComplete && data.sellerDealActive) {
          // The seller already confirmed the buyer: the pair is on the contract
          // review/sign page — resume there, not on the QR / confirm-buyer step.
          setLastId(data.id);
          setActiveStep(5);
          // The buyer was confirmed before this step, and the connect-step checks that set this
          // flag never run on a resumed page - without it the seller's price-change notices (see
          // notifyBuyerPrice) were silently dropped and the buyer's page never reacted.
          setBuyerConnected(true);
          connectToDeal(data.id).catch(console.error);
        } else if (data.carInfoId) {
          // Car is already linked → resume at QR / Submission step
          setActiveStep(3);
          // Reconnect to SignalR deal group on resume so live events are received
          connectToDeal(data.id).catch(console.error);
        } else if (data.vahicleTypeOptionId) {
          // Vehicle type chosen but no car yet → resume at car search step
          setActiveStep(2);
        }
      } catch (error) {
        console.error("❌ Error fetching ContractMain data:", error);
      }
    };

    fetchContract();
  }, [storedId]);

  useEffect(() => {
    if (activeStep !== 3 || !contractData?.id) return;

    const connectCount = countConnectMsgs(messages);
    if (connectCount < consumedConnectMsgsRef.current) consumedConnectMsgsRef.current = connectCount;
    const buyerMsg = connectCount > consumedConnectMsgsRef.current;

    if (buyerMsg && !buyerToastShown) {
      consumedConnectMsgsRef.current = connectCount;
      const fetchBuyerInfo = async () => {
        try {
          // Step 1: Get contract details
          const contractRes = await ApiCall({
            url: "/ContractMain/GetContractMainById",
            method: "GET",
            params: { Id: contractData.id },
          });

          const contractResult =
            contractRes?.result || contractRes?.data?.result;

          // QA F7: show the buyer's first+last name (falls back to username only if unset)
          const resolvedName = await resolveBuyerName(contractResult);
          if (resolvedName) setBuyerUserName(resolvedName);

          setBuyerConnected(true);

          dsToast("success", {
            title: t("seller.buyerConnectedTitle"),
            text: resolvedName || t("seller.buyerConnectedIntro"),
          }, { position: "bottom-right" });
          setBuyerToastShown(true);
        } catch (error) {
          console.error("Error fetching buyer info:", error);
        }
      };

      fetchBuyerInfo();
    }
  }, [messages, activeStep, contractData?.id, buyerToastShown]);

  // The buyer pressed "This is not the right deal": their side already cleared the
  // buyer link on the contract (same ContractMain/Update the seller's own "wrong
  // buyer" uses) and then sent this message. Restore the pre-connection stage (QR
  // + waiting) exactly like handleDisconnectBuyer does locally, and say so.
  useEffect(() => {
    if (activeStep !== 3) return;
    const left = messages.find(
      (msg) => msg.user === "buyer" && msg.message === "Buyer disconnected by buyer",
    );
    if (!left) return;

    forgetConnectedBuyer();
    setBuyerConnected(false);
    setBuyerUserName("");
    setBuyerToastShown(false);
    setSellerStartedDeal(false);
    advancingToReviewRef.current = false;
    messages.length = 0;
    dsToast("info", {
      title: t("seller.buyerDisconnectedTitle"),
      text: t("seller.buyerDisconnectedToast"),
    }, { position: "bottom-right" });
  }, [messages, activeStep, t]);

  // Fallback: on step 3 entry check API directly — covers the case where the buyer
  // connected before the seller's SignalR connection was established
  useEffect(() => {
    if (activeStep !== 3 || !contractData?.id) return;

    const checkBuyerAlreadyConnected = async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractData.id },
        });
        const latest = res?.result || res?.data?.result;
        if (!latest?.buyerDealConnected) return;

        setBuyerConnected(true);

        // QA F7: prefer the buyer's first+last name over username/email
        const resolvedName = await resolveBuyerName(latest);
        if (resolvedName) setBuyerUserName(resolvedName);
      } catch (err) {
        console.error("Error checking buyer connection on step entry:", err);
      }
    };

    checkBuyerAlreadyConnected();
  }, [activeStep, contractData?.id]);

  // Poll every 5 s while on step 3 and buyer hasn't connected yet — catches cases
  // where the SignalR "Buyer connected to Seller" message was missed
  useEffect(() => {
    if (activeStep !== 3 || !contractData?.id || buyerConnected) return;

    const poll = setInterval(async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractData.id },
        });
        const latest = res?.result || res?.data?.result;
        if (!latest?.buyerDealConnected) return;
        setBuyerConnected(true);
        const resolvedName = await resolveBuyerName(latest);
        if (resolvedName) setBuyerUserName(resolvedName);
        clearInterval(poll);
      } catch (_) {}
    }, 5000);

    return () => clearInterval(poll);
  }, [activeStep, contractData?.id, buyerConnected]);

  // Mirrors the mockup's confirmBuyer(): the seller's own click is the only
  // trigger. The moment they've confirmed a connected buyer, the fetch-merge-PUT
  // + SignalR notify runs on its own — there is no separate buyer-side click to
  // wait for (the reference has no such step; the buyer's screen just watches
  // this same process happen).
  useEffect(() => {
    if (
      activeStep !== 3 ||
      !buyerConnected ||
      !sellerStartedDeal ||
      !contractData?.id ||
      advancingToReviewRef.current
    )
      return;
    advancingToReviewRef.current = true;

    const advanceToReview = async () => {
      // Mirrors the buyer side's 2 s minimum display of the "retrieving
      // vehicle info" loading panel, so it doesn't flash-disappear when the
      // backend responds quickly.
      const MIN_LOADING_MS = 2000;
      const startedAt = Date.now();
      const waitForMinDisplay = () => {
        const remaining = MIN_LOADING_MS - (Date.now() - startedAt);
        return remaining > 0 ? new Promise((resolve) => setTimeout(resolve, remaining)) : Promise.resolve();
      };

      try {
        const response = await ApiCall({
          url: "/ContractMain/Get",
          method: "GET",
          params: { Id: contractData.id },
        });

        const contractData2 = response.result || response.data?.result;

        if (!contractData2) {
          Swal.fire("❌ Error", "Failed to fetch latest contract data!", "error");
          advancingToReviewRef.current = false;
          setSellerStartedDeal(false);
          return;
        }

        const payload = {
          ...contractData2,
          sellerDealComplete: true,
          sellerDrivingLicensePath: "",
        };

        const updateRes = await ApiCall({ url: "/ContractMain/Update", method: "PUT", data: payload });
        const status = updateRes?.status;
        const isError = updateRes?.success === false || status >= 400;

        if (!isError && (status === 200 || status === 201)) {
          const respData = updateRes?.data || updateRes;
          const returnedId = respData?.result?.id ?? respData?.id ?? null;
          if (returnedId) setLastId(returnedId);

          if (connection) {
            await sendDealMessage("seller", "Seller added valuation");
          }

          await waitForMinDisplay();
          setActiveStep(5);
        } else {
          const errorMsg =
            updateRes?.error?.details || updateRes?.error?.message || "Something went wrong while updating the contract.";
          await waitForMinDisplay();
          Swal.fire("❌ Error", errorMsg, "error");
          advancingToReviewRef.current = false;
          setSellerStartedDeal(false);
        }
      } catch (err) {
        console.error("❌ Error finalizing contract:", err);
        const errorMsg =
          err.response?.data?.error?.details || err.response?.data?.error?.message || "Unexpected server error occurred.";
        await waitForMinDisplay();
        Swal.fire("❌ Error", errorMsg, "error");
        advancingToReviewRef.current = false;
        setSellerStartedDeal(false);
      }
    };

    advanceToReview();
  }, [activeStep, buyerConnected, sellerStartedDeal, contractData?.id, connection, sendDealMessage]);

// Saves the seller's contract terms (debts / loan or lien) when they differ from the server.
const persistContractTerms = async (nextDebt, nextLien) => {
  if (!lastId) return true;
  const debt = nextDebt || "none";
  const lien = nextLien || "none";
  if (persistedTermsRef.current.debt === debt && persistedTermsRef.current.lien === lien) return true;
  try {
    const cRes = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: lastId } });
    const c = cRes?.result || cRes?.data?.result;
    if (!c) return false;
    await ApiCall({ url: "/ContractMain/Update", method: "PUT", data: { ...c, debtOption: debt, loanLienOption: lien } });
    persistedTermsRef.current = { debt, lien };
    return true;
  } catch (termErr) {
    console.error("Failed to persist contract terms", termErr);
    return false;
  }
};

// Seller must sign with BankID before the deal is finalized
const handleSellerDealConfirm = async () => {
  if (!odometerCertified) return;
  if (!lastId) {
    Swal.fire("Error", "Contract ID not found", "error");
    return;
  }
  // The terms must be on the server before the contract document is fixed for signing.
  if (!(await persistContractTerms(debtOption, value))) {
    Swal.fire("Error", "Could not save the contract terms. Please try again.", "error");
    return;
  }
  // deal log: the seller confirmed the price and terms
  logDealEvent(lastId, "price_confirmed");
  // deal log: the seller pressed the BankID signing button
  logDealEvent(lastId, "bankid_started");
  // DEV/TEST: mock signing (no BankID popup). Real BankID flow below is unchanged.
  if (BANKID_MOCK_SIGN) {
    executeSellerConfirm(await mockSignRefPrepared("seller", lastId));
    return;
  }
  setSellerSignModalOpen(true);
};

const executeSellerConfirm = async (orderRef) => {
  if (!lastId) {
    Swal.fire("Error", "Contract ID not found", "error");
    return;
  }
  if (!orderRef) {
    Swal.fire("Error", "BankID signing reference missing", "error");
    return;
  }

  setLoading(true);

  try {
    // Terms were saved before BankID started (handleSellerDealConfirm); nothing about the
    // contract may change between the BankID signature and recording it.
    const res = await ApiCall({
      url: "/ContractMain/DealCompletedBySeller",
      method: "POST",
      data: { id: lastId, orderRef },
    });

    if (res?.data?.success || res?.success) {
      // Matches the mock: no success popup and no leaving the page for one
      // party's own signature — the status line updates in place, and the
      // seller only moves on once BOTH parties have signed (see the
      // "both signed" effect below).
      setSellerSignedAt(new Date().toISOString());
      try {
        await sendDealMessage("seller", "Seller signed");
      } catch (_) {
        // best-effort SignalR notice to the buyer
      }
    } else {
      Swal.fire(
        "Failed",
        res?.data?.error?.message || "Deal confirmation failed",
        "error",
      );
    }
  } catch (error) {
    Swal.fire(
      "Error",
      error?.response?.data?.error?.message || "Something went wrong",
      "error",
    );
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    const fetchFinalContract = async () => {
      if (activeStep === 5 && lastId) {
        try {
          const response = await ApiCall({
            url: "/ContractMain/GetContractMainById",
            method: "GET",
            params: { Id: lastId },
          });

          const finalData = response?.result || response?.data?.result;

          if (finalData) {
            setFinalContract(finalData);
            setPriceValue(finalData.carValuationBySeller ? formatKr(Number(finalData.carValuationBySeller)) : "");
            // restore previously-chosen contract terms when resuming
            persistedTermsRef.current = { debt: finalData.debtOption || "none", lien: finalData.loanLienOption || "none" };
            if (finalData.debtOption) setDebtOption(finalData.debtOption);
            if (finalData.loanLienOption) setValue(finalData.loanLienOption);

            const [seller, buyer] = await Promise.all([
              getUserProfile(finalData.sellerUserId || userId),
              getUserProfile(finalData.buyerUserId),
            ]);
            if (seller) {
              setSellerProfile(seller);
              setSellerDisplayName(seller.fullName || [seller.name, seller.surname].filter(Boolean).join(" ") || seller.userName || finalData.sellerUserFullName || "");
            }
            if (buyer) {
              setBuyerProfile(buyer);
              setBuyerUserName(buyer.fullName || [buyer.name, buyer.surname].filter(Boolean).join(" ") || buyer.userName || "");
            }
          } else {
            Swal.fire(
              "⚠️ Warning",
              "Final contract data not found!",
              "warning",
            );
          }
        } catch (error) {
          console.error("❌ Error fetching final contract info:", error);
        }
      }
    };

    fetchFinalContract();
  }, [activeStep, lastId, refreshFinalContract, userId]);

  // The live "Buyer signed" notice is only a prompt to check: the buyer is shown as signed
  // only once the server has recorded their verified BankID signature (buyerSignedAt is set
  // by the backend on a verified signature, never by the client), with the server's time.
  useEffect(() => {
    if (activeStep !== 5 || !lastId) return;
    const signed = messages.find((msg) => msg.user === "buyer" && msg.message === "Buyer signed");
    if (!signed || buyerSignedHandledRef.current) return;
    (async () => {
      try {
        const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: lastId } });
        const d = res?.result || res?.data?.result;
        if (d?.buyerSignedAt && !buyerSignedHandledRef.current) {
          buyerSignedHandledRef.current = true;
          setBuyerSignedAt(d.buyerSignedAt);
        }
      } catch (_) {
        /* the periodic server check below picks it up */
      }
    })();
  }, [messages, activeStep, lastId]);

  // Pending until the other party signs: besides the live "Buyer signed" notice, ask the
  // server every few seconds so a missed message can never leave the seller waiting.
  useEffect(() => {
    if (activeStep !== 5 || !lastId || !sellerSignedAt || buyerSignedAt) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: lastId } });
        const d = res?.result || res?.data?.result;
        if (d?.buyerSignedAt) {
          buyerSignedHandledRef.current = true;
          setBuyerSignedAt(d.buyerSignedAt);
        }
      } catch (_) {
        /* keep waiting */
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [activeStep, lastId, sellerSignedAt, buyerSignedAt]);

  // Once both parties have signed (this session's own knowledge of it —
  // see above), the signed lines are the receipt — hold them on screen
  // briefly, then move on to the read-only contract page.
  //
  // leaveDealGroup/navigate/lastId are read through this ref rather than as
  // effect dependencies: those two functions are new references on most
  // renders, and depending on them here would re-run the effect on any
  // unrelated re-render in the 1200ms window, clearing the pending timer —
  // which the ref-guard above then refuses to reschedule, silently losing
  // the navigation. The effect itself only cares about the two signed
  // moments actually changing.
  const navRef = useRef({ leaveDealGroup, navigate, lastId });
  navRef.current = { leaveDealGroup, navigate, lastId };
  useEffect(() => {
    if (!sellerSignedAt || !buyerSignedAt) return;
    if (bothSignedNavigateRef.current) return;
    bothSignedNavigateRef.current = true;
    const timer = setTimeout(async () => {
      const { leaveDealGroup, navigate, lastId } = navRef.current;
      if (leaveDealGroup) await leaveDealGroup();
      // The signed agreement is on /Contract/:id for the record; what the seller needs now is
      // the payment stage, which is where the post-signing flow goes.
      navigate(`/Payment/${lastId}`);
    }, 1200);
    return () => clearTimeout(timer);
  }, [sellerSignedAt, buyerSignedAt]);

  // ------------------------------------------

  const validateSaleForm = () => {
    let ok = true;
    if (!registration.trim()) {
      setRegError(t("seller.error.regRequired"));
      ok = false;
    }
    if (!priceValue) {
      setPriceError(t("seller.error.priceRequired"));
      ok = false;
    }
    if (!registration.trim()) {
      regInputRef.current?.focus();
    } else if (!priceValue) {
      priceInputRef.current?.focus();
    }
    return ok;
  };

  // Gate on an unusually-low price before actually submitting (handleSearch
  // does the real create/link + backend work below, unchanged).
  const handleCreateSaleSubmit = () => {
    if (!validateSaleForm()) return;

    const numericPrice = Number(priceValue.replace(/\s/g, ""));
    if (!isUnusuallyLowPrice(numericPrice)) {
      handleSearch();
      return;
    }

    Swal.fire({
      html: `
        <div class="ds-swal-price">
          <p class="ds-swal-price-title">${escapeHtml(t("seller.priceWarning.title"))}</p>
          <p class="ds-swal-price-desc">${escapeHtml(t("seller.priceWarning.body", { price: formatKr(numericPrice) }))}</p>
          <p class="ds-swal-price-note">${escapeHtml(t("seller.priceWarning.reference", { estimate: formatKr(PRICE_WARNING_CONFIG.estimatedValue) }))}</p>
          <p class="ds-swal-price-subtext">${escapeHtml(t("seller.priceWarning.sellerOnly"))}</p>
          <div class="ds-swal-price-actions">
            <button type="button" class="ds-swal-price-cancel" id="ds-swal-pricewarn-useanyway">${escapeHtml(t("seller.priceWarning.confirm"))}</button>
            <button type="button" class="ds-swal-price-save" id="ds-swal-pricewarn-edit">${escapeHtml(t("seller.priceWarning.edit"))}</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
      didOpen: (popup) => {
        popup.querySelector("#ds-swal-pricewarn-edit").addEventListener("click", () => {
          Swal.close();
          priceInputRef.current?.focus();
          priceInputRef.current?.select();
        });
        popup.querySelector("#ds-swal-pricewarn-useanyway").addEventListener("click", () => {
          Swal.close();
          handleSearch();
        });
      },
    });
  };

  const handleSearch = async () => {
    if (!validateSaleForm()) return;
    // A new deal needs a BankID-verified seller: verify first (before the paid registry lookup),
    // then this search carries on with what was entered.
    if (!contractData?.id && !(await ensureVerified())) return;

    setLoading(true);
    setError("");
    setBuyerConnected(false);
    setBuyerToastShown(false);
    messages.length = 0;

    try {
      // Canonical form: the registry returns nothing for "CCS 233" or "CCS-233", and the car row
      // is keyed the same way, so the plate is stripped to its bare characters before it is used.
      const inputReg = normalizeRegistration(registration);

      // Stop right away (before the paid registry lookup) if this seller already has an open
      // deal — the backend answers with a clear message that the catch below displays.
      await ApiCall({
        url: "/ContractMain/EnsureNoOpenDeal",
        method: "POST",
        data: { exceptContractId: contractData?.id || 0, isBuyer: false },
      });

      // 1. Look up the vehicle from the external registry (Bilvision) so the
      // car record — and everywhere it's shown, like the contract masthead's
      // "<name> <year>" line — carries the real designation/year instead of
      // staying blank. The lookup is required: if the registry call fails
      // (e.g. the Bilvision subscription/agreement has expired), the seller
      // is stopped here and not allowed to create a car with no vehicle data.
      let vehicleInfo = null;
      try {
        const vehicleInfoRes = await ApiCall({
          url: "/CarInfo/GetVehicleInfo",
          method: "GET",
          params: { Id: inputReg },
        });
        const vehicleInfoList = vehicleInfoRes?.result || vehicleInfoRes?.data?.result;
        vehicleInfo = Array.isArray(vehicleInfoList) ? vehicleInfoList[0] : null;
      } catch (err) {
        console.error("❌ Car info lookup failed:", err);
        // A plate the registry doesn't know is NOT an error here — the backend
        // returns an empty list for it and the "vehicle not found" branch below
        // handles it. This catch is only for a genuine lookup failure, so show
        // the backend's own reason (it sends a friendly title/details for
        // registry/credential failures) rather than assuming the subscription.
        const backendError = err?.response?.data?.error;
        Swal.fire({
          icon: "error",
          title: backendError?.details ? backendError.message : "Car Info API Error",
          text: backendError?.details || "The vehicle lookup service is unavailable right now. Please try again in a moment.",
          ...SWAL_THEME,
        });
        return;
      }

      // The registry call can succeed but return no match (unregistered /
      // unknown plate, or a vehicle that isn't available at all) — in that
      // case vehicleInfo is null. Without this guard the code below went on
      // to create a car/contract with every field blank (just the typed-in
      // registration number), which is the empty rows the seller then saw.
      if (!vehicleInfo) {
        Swal.fire({
          icon: "error",
          title: t("seller.error.vehicleNotFoundTitle"),
          text: t("seller.error.vehicleNotFoundText"),
          ...SWAL_THEME,
        });
        return;
      }

      const fordon = vehicleInfo?.fordonsuppgifter;
      const agare = vehicleInfo?.fordonBrukareAgare;
      const teknisk = vehicleInfo?.tekniskData;
      const miljo = vehicleInfo?.miljoklassning;
      const besiktning = vehicleInfo?.besiktning;
      const skatt = vehicleInfo?.beraknat?.skatt;

      // 2. Create/link the car with whatever the registry returned.
      const carCreateRes = await ApiCall({
        url: "/CarInfo/Create",
        method: "POST",
        data: {
          registrationNo: inputReg,
          vehicleDesignation: fordon?.fordonsbenamning ?? undefined,
          tradeName: fordon?.handelsbeteckning ?? undefined,
          yearModel: fordon?.arsmodell ?? undefined,
          vehicleYears: fordon?.fordonsar != null ? String(fordon.fordonsar) : undefined,
          registrationDate: fordon?.registreringsdatum ?? undefined,
          numberOfUsers: agare?.antalBrukare != null ? String(agare.antalBrukare) : undefined,
          waxelbarge: teknisk?.vaxellada ?? undefined,
          fourWheelDrive: !!teknisk?.fyrhjulsdrift,
          fuel: teknisk?.drivmedel?.map((d) => d.drivmedel).filter(Boolean).join(", ") || undefined,
          electricVehicleConfiguration: miljo?.elfordonKonfiguration ?? undefined,
          inspectionDate: besiktning?.besiktningsdatum ?? undefined,
          inspectionDateEMPTY: besiktning?.besiktningsdatumTOM ?? undefined,
          dispensationInspectionGroup: besiktning?.dispensbesiktningsgrupp ?? undefined,
          inspectionGroup: besiktning?.besiktningsgrupp ?? undefined,
          feedingStall: besiktning?.matarstallning != null ? String(besiktning.matarstallning) : undefined,
          inspectionStation: besiktning?.besiktningsstation ?? undefined,
          inspectionProgramCode: besiktning?.besiktningsprogramkod != null ? String(besiktning.besiktningsprogramkod) : undefined,
          previousInspectionDate: besiktning?.foregaendeBesiktningsdatum ?? undefined,
          previousInspectionProgramCode:
            besiktning?.foregaendeBesiktningsprogramkod != null ? String(besiktning.foregaendeBesiktningsprogramkod) : undefined,
          tax: skatt?.skatt != null ? String(skatt.skatt) : undefined,
          malus: skatt?.malus != null ? String(skatt.malus) : undefined,
        },
      });

      const newCar = carCreateRes?.result || carCreateRes?.data?.result;
      if (!newCar?.id) throw new Error("Car created but ID not received.");

      // 3. The registry lookup succeeded and the car exists — only NOW is the
      // contract created (never before a successful lookup), unless this seller
      // is resuming a contract that already exists.
      let contractId = contractData?.id;
      if (!contractId) {
        const createRes = await ApiCall({
          url: "/ContractMain/Create",
          method: "POST",
          data: {
            sellerUserId: userId,
            sellerDealStatus: "Seller",
            sellerDealActive: true,
            sellerDealComplete: false,
            vahicleTypeOptionId: pendingVehicleType?.id ?? null,
            vahicleTypeOptionName: pendingVehicleType?.name ?? "",
            sellerDrivingLicensePath: "",
            sellerDrivingLicenseValidate: true,
            carInfoId: newCar.id,
            carValuationBySeller: 0,
          },
        });
        const created = createRes?.result || createRes?.data?.result;
        if (!created?.id) throw new Error("Failed to start the deal.");
        contractId = created.id;
        localStorage.setItem("currentContractID", created.id);
      }

      const contractRes = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
        params: { Id: contractId },
      });

      const latestContract = contractRes?.result || contractRes?.data?.result;
      if (!latestContract) throw new Error("Failed to fetch contract data.");
      setContractData({ ...latestContract, carInfoId: newCar.id });

      // 4. Update contract with car info + the seller's asking price
      const contractPayload = buildCarLinkPayload({ latestContract, newCar, priceValue });

      await ApiCall({ url: "/ContractMain/Update", method: "PUT", data: contractPayload });

      // Set registration number for display on step 3
      setCarRegNo(newCar.registrationNo || inputReg);

      // 5. Connect to SignalR deal group
      await connectToDeal(contractId);

      setActiveStep(3);
    } catch (err) {
      console.error(err);
      const backendError = err?.response?.data?.error;
      if (backendError) {
        Swal.fire({
          icon: "error",
          title: backendError.message || "Server Error",
          text: backendError.details || "Something went wrong on the server",
          ...SWAL_THEME,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.message || "Unexpected error",
          ...SWAL_THEME,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Same fetch-merge-PUT pattern as every other contract mutation in this
  // file — no new endpoint, just ContractMain/Update with the new price.
  // Tell a connected buyer (over the existing deal group) what the price dialog is
  // doing so their page can lock / reload. No buyer connected -> nothing to tell:
  // the new price is simply saved and the buyer reads it when they connect.
  const notifyBuyerPrice = async (message) => {
    // On the contract step the buyer has already been confirmed, whatever this page remembers.
    if (!(buyerConnected || activeStep === 5) || !connection) return;
    try {
      await sendDealMessage("seller", message);
    } catch (err) {
      console.error("Failed to notify buyer of price change:", err);
    }
  };

  const handleChangePrice = async () => {
    const activeContract = finalContract || contractData;
    if (!activeContract?.id) return;

    // Built as plain html (rather than SweetAlert2's own input/actions
    // plumbing) so the layout is exact: left-aligned copy, a label above the
    // input, a "kr" suffix beside it, and a right-aligned button row.
    await notifyBuyerPrice(PRICE_MSG.changing);
    const result = await Swal.fire({
      html: `
        <div class="ds-swal-price ds-swal-price-edit">
          <p class="ds-swal-price-title">${escapeHtml(t("seller.changePrice"))}</p>
          <p class="ds-swal-price-desc">${escapeHtml(t("seller.changePriceDescription"))}</p>
          <label class="ds-swal-price-label" for="ds-swal-price-input">${escapeHtml(t("seller.price"))}</label>
          <div class="ds-swal-price-row">
            <input id="ds-swal-price-input" class="ds-swal-price-input" inputmode="numeric" autocomplete="off" value="${escapeHtml(priceValue)}" />
            <span class="ds-swal-price-suffix">kr</span>
          </div>
          <p class="ds-swal-price-error" id="ds-swal-price-error" style="display:none"></p>
          <div class="ds-swal-price-actions ds-swal-price-actions--start">
            <button type="button" class="ds-swal-price-save" id="ds-swal-price-save">${escapeHtml(t("seller.savePriceCta"))}</button>
            <button type="button" class="ds-swal-price-cancel" id="ds-swal-price-cancel">${escapeHtml(t("actions.cancel"))}</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
      didOpen: (popup) => {
        const input = popup.querySelector("#ds-swal-price-input");
        const errorEl = popup.querySelector("#ds-swal-price-error");
        const cancelBtn = popup.querySelector("#ds-swal-price-cancel");
        const saveBtn = popup.querySelector("#ds-swal-price-save");

        input.focus();
        input.select();

        cancelBtn.addEventListener("click", () => Swal.close());
        saveBtn.addEventListener("click", () => {
          const raw = input.value.replace(/\s/g, "");
          if (!raw || !/^\d+$/.test(raw)) {
            errorEl.textContent = t("seller.error.priceRequired");
            errorEl.style.display = "block";
            input.focus();
            return;
          }
          Swal.close({ isConfirmed: true, value: raw });
        });
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") saveBtn.click();
        });
      },
    });

    if (!result.isConfirmed) {
      await notifyBuyerPrice(PRICE_MSG.cancelled);
      return;
    }

    const raw = result.value;
    const formatted = formatKr(Number(raw));

    try {
      const contractRes = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
          params: { Id: activeContract.id },
      });
      const latestContract = contractRes?.result || contractRes?.data?.result;
      if (!latestContract) throw new Error("Failed to fetch contract data.");

      await ApiCall({
        url: "/ContractMain/Update",
        method: "PUT",
        data: { ...latestContract, carValuationBySeller: raw, sellerDrivingLicensePath: "" },
      });

      setPriceValue(formatted);
      setFinalContract((current) => current ? { ...current, carValuationBySeller: Number(raw) } : current);
      await notifyBuyerPrice(PRICE_MSG.updated);
      setRefreshFinalContract((current) => !current);
    } catch (err) {
      await notifyBuyerPrice(PRICE_MSG.cancelled);
      Swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to update price", ...SWAL_THEME });
    }
  };

  const openContractPriceEditor = async () => {
    const currentPrice = finalContract?.carValuationBySeller ?? contractData?.carValuationBySeller ?? "";
    setContractPriceDraft(String(currentPrice || ""));
    setContractPriceError("");
    setContractPriceEditing(true);
    await notifyBuyerPrice(PRICE_MSG.changing);
  };

  const cancelContractPriceEditor = async () => {
    setContractPriceEditing(false);
    setContractPriceError("");
    await notifyBuyerPrice(PRICE_MSG.cancelled);
  };

  const saveContractPrice = async (event) => {
    event.preventDefault();
    const activeContract = finalContract || contractData;
    const raw = contractPriceDraft.replace(/\s/g, "");
    if (!raw || !/^\d+$/.test(raw)) {
      setContractPriceError(t("seller.error.priceRequired"));
      return;
    }
    if (!activeContract?.id) return;

    setContractPriceSaving(true);
    setContractPriceError("");
    try {
      const contractRes = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
        params: { Id: activeContract.id },
      });
      const latestContract = contractRes?.result || contractRes?.data?.result;
      if (!latestContract) throw new Error("Failed to fetch contract data.");

      await ApiCall({
        url: "/ContractMain/Update",
        method: "PUT",
        data: { ...latestContract, carValuationBySeller: raw, sellerDrivingLicensePath: "" },
      });
      setPriceValue(formatKr(Number(raw)));
      setFinalContract((current) => current ? { ...current, carValuationBySeller: Number(raw) } : current);
      setContractPriceEditing(false);
      await notifyBuyerPrice(PRICE_MSG.updated);
      setRefreshFinalContract((current) => !current);
    } catch (err) {
      await notifyBuyerPrice(PRICE_MSG.cancelled);
      setContractPriceError(err.message || "Failed to update price");
    } finally {
      setContractPriceSaving(false);
    }
  };

  // The actual disconnect mutation (ContractMain/Update clearing the buyer
  // link + SignalR notice + local reset), split out of handleDisconnectBuyer
  // so the session-timeout "wait or disconnect" dialog below can run it
  // directly — that dialog is already the confirmation, it doesn't need a
  // second nested "are you sure?" on top.
  const disconnectBuyerNow = async ({ wrongBuyer = false } = {}) => {
    if (!contractData?.id) return;
    try {
      if (wrongBuyer) {
        // "Wrong buyer?" only: its own endpoint, because it means this person was never part of
        // the sale — the server removes the buyer link entirely and records the episode in the
        // deal log. The timeout dialog below keeps the plain Update, where the buyer did take
        // part and their record of the deal stands.
        await ApiCall({
          url: "/ContractMain/DisconnectWrongBuyer",
          method: "POST",
          data: { id: contractData.id },
        });
      } else {
        const contractRes = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: contractData.id },
        });
        const latestContract = contractRes?.result || contractRes?.data?.result;
        if (!latestContract) throw new Error("Failed to fetch contract data.");

        await ApiCall({
          url: "/ContractMain/Update",
          method: "PUT",
          data: { ...latestContract, buyerDealConnected: false, buyerUserName: "", buyerName: "" },
        });
      }

      if (connection) {
        await sendDealMessage("seller", "Buyer disconnected by seller");
      }

      forgetConnectedBuyer();
      setBuyerConnected(false);
      setBuyerUserName("");
      setBuyerToastShown(false);
      setSellerStartedDeal(false);
      advancingToReviewRef.current = false;
      messages.length = 0;
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to disconnect buyer", ...SWAL_THEME });
    }
  };

  // Resets the contract's buyer link server-side (same ContractMain/Update
  // endpoint) and clears local connection state, so the seller's QR/reg
  // number keep working for the next buyer to connect.
  const handleDisconnectBuyer = async () => {
    if (!contractData?.id) return;

    // Same custom-html approach as the change-price dialog (see there for
    // why) — left-aligned copy, a right-aligned button row with no stray
    // spacing, instead of fighting SweetAlert2's centered input/actions.
    const confirmResult = await Swal.fire({
      html: `
        <div class="ds-swal-price ds-swal-price-confirm">
          <p class="ds-swal-price-title">${escapeHtml(t("seller.confirmDisconnect.title"))}</p>
          <p class="ds-swal-price-desc">${escapeHtml(t("seller.confirmDisconnect.body", { name: buyerUserName || "" }))}</p>
          <div class="ds-swal-price-actions">
            <button type="button" class="ds-swal-price-cancel" id="ds-swal-disc-cancel">${escapeHtml(t("seller.confirmDisconnect.cancel"))}</button>
            <button type="button" class="ds-swal-price-disconnect" id="ds-swal-disc-confirm">${escapeHtml(t("seller.confirmDisconnect.confirm"))}</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
      didOpen: (popup) => {
        popup.querySelector("#ds-swal-disc-cancel").addEventListener("click", () => Swal.close());
        popup.querySelector("#ds-swal-disc-confirm").addEventListener("click", () => Swal.close({ isConfirmed: true }));
      },
    });

    if (!confirmResult.isConfirmed) return;
    await disconnectBuyerNow({ wrongBuyer: true });
  };

  // Same fetch-merge-PUT pattern, setting the same `dealCancel` flag the
  // resume effect above already checks (localStorage.currentContractID →
  // ChooseAction) to drop stale/cancelled sales — no new backend concept.
  // Afterwards the seller lands straight back on "Start Your Sale" (step 2) with
  // the vehicle type carried over (no contract is created until a successful lookup), instead of
  // being routed through ChooseAction and vehicle-type selection again.
  const handleCancelSale = async () => {
    if (!contractData?.id) return;

    const result = await Swal.fire({
      html: `
        <div class="ds-swal-price ds-swal-price-confirm">
          <p class="ds-swal-price-title">${escapeHtml(t("seller.confirmCancel.title"))}</p>
          <p class="ds-swal-price-desc">${escapeHtml(t("seller.confirmCancel.body"))}</p>
          <div class="ds-swal-price-actions">
            <button type="button" class="ds-swal-price-cancel" id="ds-swal-cancelsale-keep">${escapeHtml(t("seller.confirmCancel.cancel"))}</button>
            <button type="button" class="ds-swal-price-disconnect" id="ds-swal-cancelsale-confirm">${escapeHtml(t("seller.confirmCancel.confirm"))}</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
      didOpen: (popup) => {
        popup.querySelector("#ds-swal-cancelsale-keep").addEventListener("click", () => Swal.close());
        popup.querySelector("#ds-swal-cancelsale-confirm").addEventListener("click", () => Swal.close({ isConfirmed: true }));
      },
    });

    if (!result.isConfirmed) return;

    try {
      const contractRes = await ApiCall({
        url: "/ContractMain/GetContractMainById",
        method: "GET",
        params: { Id: contractData.id },
      });
      const latestContract = contractRes?.result || contractRes?.data?.result;
      if (!latestContract) throw new Error("Failed to fetch contract data.");

      await ApiCall({
        url: "/ContractMain/Update",
        method: "PUT",
        data: { ...latestContract, dealCancel: true },
      });

      // The sale is over: tell a connected buyer and drop this tab's deal connection, so the
      // "Leave the deal?" guard no longer applies once the seller is back on the search step.
      await announceDealCancelled(contractData.id);

      // No replacement contract is created here: the cancelled sale is dropped and
      // a new one is only created after the next successful vehicle lookup.
      localStorage.removeItem("currentContractID");
      setStoredId(null);
      setContractData(null);
      setPendingVehicleType({
        id: latestContract.vahicleTypeOptionId ?? null,
        name: latestContract.vahicleTypeOptionName ?? "",
      });
      setLastId(null);
      setCarRegNo("");
      setRegistration("");
      setPriceValue("");
      setRegError("");
      setPriceError("");
      setBuyerConnected(false);
      setBuyerUserName("");
      setBuyerToastShown(false);
      setQR("");
      messages.length = 0;
      setActiveStep(2);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to cancel sale", ...SWAL_THEME });
    }
  };

  return (
    <PublicPage>
      {verificationDialogs}
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          {/* Reference (Index.tsx) shows the progress rail only once inside the
              real flow (registration entry onward) — the vehicle-type step
              itself is not wrapped in FlowStage and carries no rail. */}
          {activeStep >= 1 && (
            <div className="flow-shell-track">
              <SellerFlowTrack activeStep={activeStep} t={t} />
            </div>
          )}

          {/* ------------------ STEP 0 — choose vehicle type ------------------ */}
          {activeStep < 1 && (
            <Formik
              initialValues={{
                vahicleTypeOptionId: null,
                vahicleTypeOptionName: "",
                vahicleTypeOptionVahicleTypeName: "",
              }}
              validationSchema={validationSchemas[activeStep]}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                try {
                  setLoading(true);
                  setSubmitting(true);

                  // Client-side only: do NOT create or update a contract here.
                  setPendingVehicleType({ id: values.vahicleTypeOptionId, name: values.vahicleTypeOptionName });
                  resetForm();
                  setActiveStep(2);
                } catch (err) {
                  Swal.fire(
                    "❌ Error",
                    err.message || "Submission failed.",
                    "error",
                  );
                } finally {
                  setLoading(false);
                  setSubmitting(false);
                }
              }}
            >
              {({ submitForm, setFieldValue }) => (
                <Form>
                  <div className="mx-auto w-full max-w-5xl">
                    <h1 className="ds-title-step mb-8 text-center">
                      {t("seller.chooseVehicleTitle")}
                    </h1>

                    {loading ? (
                      <div className="flex justify-center py-12" role="status" aria-label={t("seller.fetchingCarDetails")}>
                        <div
                          className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
                          style={{ borderTopColor: "var(--ds-green)" }}
                        />
                      </div>
                    ) : error ? (
                      <p className="ds-body py-6 text-center font-semibold text-red-600">
                        {error}
                      </p>
                    ) : vehicleData.length === 0 ? (
                      <p className="ds-body py-6 text-center font-semibold">
                        {t("seller.noVehicleTypes")}
                      </p>
                    ) : (
                      // Keyed on the loaded data (not just a static class) so the
                      // entrance animation plays exactly when the cards actually
                      // mount, instead of on the page shell before the async
                      // /VahicleTypeOption/GetAll response arrives.
                      <div key="vehicle-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-3 motion-safe:animate-fade-in">
                        {vehicleData.map((veh) => {
                          const kind = visualKindFor(veh.vahicleTypeName);
                          return (
                            <button
                              key={veh.id}
                              type="button"
                              onClick={() => {
                                setFieldValue("vahicleTypeOptionId", veh.id);
                                setFieldValue(
                                  "vahicleTypeOptionName",
                                  veh.vahicleTypeName,
                                );
                                setFieldValue(
                                  "vahicleTypeOptionVahicleTypeName",
                                  veh.vahicleTypeName,
                                );
                                setVehicleTypeName(veh.vahicleTypeName);

                                setTimeout(() => {
                                  submitForm();
                                }, 0);
                              }}
                              className="ds-object-press group flex flex-col overflow-hidden rounded-[13px] border border-ds-rule-edge bg-ds-sheet text-left shadow-[0_1px_2px_rgba(16,30,40,.05)]"
                            >
                              {kind ? (
                                <span className="flex w-full items-end justify-center px-8 pb-1 pt-7" aria-hidden="true">
                                  <VehicleTypeVisual kind={kind} className="max-w-[220px]" />
                                </span>
                              ) : (
                                <span className="flex w-full items-center justify-center py-7" aria-hidden="true">
                                  <span
                                    className="flex h-14 w-14 items-center justify-center rounded-full"
                                    style={{ background: "var(--ds-green-bg)" }}
                                  >
                                    {vehicleIconFor(veh.vahicleTypeName)}
                                  </span>
                                </span>
                              )}
                              <span className="flex min-h-14 items-center justify-between border-t border-ds-rule-row px-5 py-4">
                                <span className="font-ds-serif text-lg font-semibold text-ds-ink">
                                  {vehicleLabelFor(veh.vahicleTypeName, t)}
                                </span>
                                <span
                                  className="flex items-center gap-2 font-ds-sans text-sm font-medium text-ds-text transition-colors group-hover:text-ds-ink"
                                  aria-hidden="true"
                                >
                                  {t("seller.selectCta")} <ForwardArrow stroke="currentColor" width={15} height={11} />
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div
                      className="mx-auto mt-5 flex max-w-xl flex-col justify-center gap-2 sm:flex-row"
                      aria-label={t("nav.comingSoon")}
                    >
                      {[
                        { kind: "camper", label: t("seller.futureVehicles.camper") },
                        { kind: "lightTruck", label: t("seller.futureVehicles.lightTruck") },
                      ].map((fv) => (
                        <div
                          key={fv.kind}
                          className="flex min-w-0 items-center gap-3 rounded-[9px] border border-ds-rule-row bg-ds-sheet px-4 py-2.5 text-ds-muted opacity-60"
                        >
                          <VehicleTypeVisual kind={fv.kind} className="w-[76px] flex-none sm:w-[84px]" />
                          <span className="min-w-0">
                            <span className="block font-ds-sans text-sm font-semibold text-ds-text">{fv.label}</span>
                            <span className="block font-ds-sans text-xs text-ds-muted">{t("nav.comingSoon")}</span>
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => guardedNavigate("/user/ChooseAction")}
                        className="ds-action-text py-[15px]"
                      >
                        <BackArrow />
                        <span className="ds-underline">{t("seller.backToOverview")}</span>
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {/* ------------------ STEP 2 — registration number & price ------------------ */}
          {activeStep === 2 && (
            <div className="ds-form mx-auto w-full max-w-2xl">
              <div className="mb-3 -mt-2">
                <button type="button" onClick={() => guardedNavigate("/user/ChooseAction")} className="ds-action-text">
                  <BackArrow />
                  <span>{t("seller.backToStart")}</span>
                </button>
              </div>

              <div className="flex flex-col gap-5">
                <div>
                  <h1 className="ds-title-step">{t("seller.startSaleTitle")}</h1>
                  <p className="ds-body mt-1.5 text-ds-muted">{t("seller.startSaleSubtitle")}</p>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleCreateSaleSubmit();
                  }}
                  noValidate
                  className="flex flex-col gap-4 sm:max-w-[528px]"
                >
                  <div>
                    <label htmlFor="seller-reg" className="ds-label block">
                      {t("seller.regNumberLabel")}
                    </label>
                    <input
                      id="seller-reg"
                      ref={regInputRef}
                      className="ds-input ds-input--mono mt-1.5 h-11 uppercase text-[17px] font-semibold tracking-[0.06em]"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      value={registration}
                      onChange={(e) => {
                        setRegistration(e.target.value.toUpperCase());
                        if (regError) setRegError("");
                      }}
                      aria-invalid={!!regError}
                      aria-describedby={regError ? "seller-reg-error" : undefined}
                    />
                    {regError && (
                      <p id="seller-reg-error" role="alert" className="ds-error-text mt-2">
                        {regError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="seller-price" className="ds-label block">
                      {t("seller.priceLabel")}
                    </label>
                    <div className="relative mt-1.5">
                      <input
                        id="seller-price"
                        ref={priceInputRef}
                        inputMode="numeric"
                        className="ds-input h-11 pr-12 text-[17px] tabular-nums"
                        autoComplete="off"
                        value={priceValue}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\s/g, "");
                          if (!/^\d*$/.test(raw)) return;
                          const formatted = raw ? formatKr(Number(raw)) : "";
                          setPriceValue(formatted);
                          if (priceError) setPriceError("");
                        }}
                        aria-invalid={!!priceError}
                        aria-describedby={priceError ? "seller-price-error" : undefined}
                      />
                      <span
                        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[15px] text-ds-muted"
                        aria-hidden="true"
                      >
                        kr
                      </span>
                    </div>
                    {priceError && (
                      <p id="seller-price-error" role="alert" className="ds-error-text mt-2">
                        {priceError}
                      </p>
                    )}
                  </div>

                  {error && (
                    <p role="alert" className="ds-error-text">
                      {error}
                    </p>
                  )}

                  <div className="mt-3 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-7">
                    <button
                      type="submit"
                      disabled={loading}
                      className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[220px] disabled:opacity-60"
                    >
                      {loading ? <span className="ds-mono text-[13px]">...</span> : t("seller.createSaleCta")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ------------------ STEP 3 — sale created / waiting for buyer ------------------ */}
          {/* Restyled to the reference's connection surface (DealFlowConnected.tsx):
              a fixed deal band (plate, type, price), a heading whose words change
              in place, and one quiet secondary-actions row — instead of the card
              paradigm this step used before. Matches the reference's confirmBuyer()
              exactly: the seller's own click is the only trigger — it flips
              sellerStartedDeal, which alone (no separate buyer-side click) starts
              the "preparing the contract" fetch-merge-PUT below. handleChangePrice/
              handleCancelSale/handleDisconnectBuyer/setSellerStartedDeal are still
              the same functions wired to the same actions. */}
          {activeStep === 3 && (
            <div className="ds-form mx-auto w-full max-w-2xl">
              <div
                className="mb-8 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-t-2 border-ds-ink pt-4"
                data-testid="deal-band"
              >
                <div className="flex min-w-0 items-center gap-4">
                  {carRegNo && <RegistrationPlate value={carRegNo} size="row" />}
                  {vehicleTypeName && (
                    <span className="text-[15px] font-semibold text-ds-ink">{vehicleTypeName}</span>
                  )}
                </div>
                {priceValue && (
                  <div className="text-right">
                    <div className="text-[12.5px] font-medium text-ds-muted">{t("seller.price")}</div>
                    <div className="mt-0.5 text-[17px] font-semibold tabular-nums text-ds-ink">
                      {priceValue} kr
                    </div>
                  </div>
                )}
              </div>

              <h1
                tabIndex={-1}
                className="m-0 text-[clamp(24px,2.6vw,30px)] font-bold leading-[1.12] text-ds-ink focus:outline-none"
              >
                {sellerStartedDeal
                  ? t("agreement.retrievingTitle")
                  : buyerConnected
                    ? t("seller.buyerConnectedTitle")
                    : t("seller.SaleCreated")}
              </h1>

              {/* The seller has confirmed: the same "preparing" surface the
                  reference shows while it fetches the vehicle data and opens
                  the contract — a single lead line led by the breathing dot,
                  never a separate title. */}
              {sellerStartedDeal ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="mt-3 flex max-w-[54ch] items-start gap-3 text-[15px] leading-[1.55] text-ds-text"
                >
                  <span
                    className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-ds-green motion-safe:animate-pulse"
                    aria-hidden="true"
                  />
                  <span>{t("agreement.retrievingBody")}</span>
                </p>
              ) : (
                <p className="ds-body mt-3 max-w-[54ch] text-ds-text">
                  {buyerConnected
                    ? t("seller.buyerConnectedIntro")
                    : t("seller.saleCreatedIntro", { reg: formatRegNumber(carRegNo) })}
                </p>
              )}

              {/* The buyer has joined: named once, directly under the lead,
                  with the seller's decision about them (confirm/wrong buyer)
                  right there too — before the code, matching the reference,
                  which puts the current TASK above the code kept only as
                  context underneath. Only while there is still a decision to
                  make: once the seller has started the deal this is settled
                  and the surface moves on. */}
              {buyerConnected && !sellerStartedDeal && (
                <div className="mt-8">
                  <p
                    role="status"
                    aria-live="polite"
                    className="m-0 flex items-start gap-3 text-[15px] leading-[1.55] text-ds-text"
                  >
                    <span className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-ds-green" aria-hidden="true" />
                    <span className="font-semibold text-ds-ink">{buyerUserName}</span>
                  </p>
                  <div className="mt-5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-7">
                    <button
                      type="button"
                      onClick={async () => {
                        setSellerStartedDeal(true);
                        // Tell the buyer they've been confirmed — without this
                        // message the buyer's own screen had no way to know and
                        // was advancing itself on a fixed local timer instead.
                        if (connection) {
                          try {
                            await sendDealMessage("seller", "Seller confirmed buyer");
                          } catch (err) {
                            console.error("Failed to notify buyer of confirmation:", err);
                          }
                        }
                      }}
                      className="ds-action-ink w-full justify-center sm:w-auto sm:min-w-[220px]"
                    >
                      {t("seller.startDeal")}
                    </button>
                    <button type="button" onClick={handleDisconnectBuyer} className="ds-action-text justify-center">
                      <span className="ds-underline">{t("seller.wrongBuyer")}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* The same QR object, unmoved, before and after the buyer joins —
                  it only leaves once the seller has started the deal. */}
              {!sellerStartedDeal && (
                <div className="mt-8">
                  <div className="inline-block rounded-[13px] border border-ds-rule-edge bg-white p-4 align-top">
                    {QR ? (
                      <QRCode value={QR} size={224} level="M" bgColor="#ffffff" fgColor="#14242e" />
                    ) : (
                      <span className="flex h-[224px] w-[224px] items-center justify-center text-center text-sm text-ds-muted">
                        {t("seller.fetchingCarDetails")}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* "Wrong buyer?" now lives above, next to "Confirm the buyer"
                  (matching the reference's ActionRow) instead of being
                  duplicated here too; cancelling the sale only makes sense
                  before anyone has connected. Once the seller has confirmed,
                  the surface matches the reference's "preparing" state exactly
                  (nothing on screen but the heading and lead) — there is
                  nothing left to change or cancel while the contract is being
                  prepared. */}
              {!sellerStartedDeal && (
                <div className="mt-8 flex flex-wrap items-center gap-x-7 border-t border-ds-rule-row pt-2">
                  <button type="button" onClick={handleChangePrice} className="ds-action-text">
                    <span className="ds-underline">{t("seller.changePrice")}</span>
                  </button>
                  {!buyerConnected && (
                    <button type="button" onClick={handleCancelSale} className="ds-action-text">
                      <span className="ds-underline">{t("seller.cancelSale")}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ------------------ STEP 5 — review & confirm ------------------ */}
          {activeStep === 5 && (
            <div className="ds-form ds-contract-review ds-contract-review--seller mx-auto w-full">
              {!finalContract ? (
                <div className="overflow-hidden rounded-[13px] border border-ds-rule-edge bg-ds-sheet p-2">
                  {Array.from({ length: 12 }).map((_, idx) => (
                    <div key={idx} className="mx-3 my-2 h-4 animate-pulse rounded bg-[rgba(20,36,46,.08)]" style={{ width: "60%" }} />
                  ))}
                </div>
              ) : (
                <article className="ds-doc ds-doc--terms">
                  <ContractMasthead
                    t={t}
                    regNo={finalContract.carInfoRegistrationNo}
                    vehicleDesignation={finalContract.carInfoVehicleDesignation}
                    vehicleYears={finalContract.carInfoVehicleYears}
                    vehicleType={finalContract.vahicleTypeOptionName}
                    sellerName={sellerDisplayName}
                    buyerName={buyerUserName}
                    lastChangedAt={finalContract.lastModificationTime || finalContract.creationTime}
                  />

                  <Clause
                    id="section-vehicle"
                    number={1}
                    title={t("seller.vehicleInformation")}
                    aside={<ContractSourceButton t={t} contract={finalContract} referenceLayout />}
                  >
                    <Register>
                      {[
                        // Mock-up-only concepts we have no database column for —
                        // shown anyway, always empty, per the mock-up's own field set.
                        ["vin", null],
                        ["color", null],
                        // Every column on tbl_CarInfoMain, in the same shape the
                        // database stores it — nothing hidden, nothing renamed away.
                        ["registrationNo", finalContract.carInfoRegistrationNo],
                        ["vehicleDesignation", finalContract.carInfoVehicleDesignation],
                        ["tradeName", finalContract.carInfoTradeName],
                        // "Year model" is the primary model-year field; Bilvision's
                        // Årsmodell (YearModel) is often blank, so fall back to
                        // Fordonsår (VehicleYears) rather than showing both/neither.
                        ["yearModel", finalContract.carInfoYearModel || finalContract.carInfoVehicleYears],
                        ["engine", null],
                        ["fuel", finalContract.carInfoFuel],
                        ["transmission", finalContract.carInfoWaxelbarge],
                        [
                          "drivetrain",
                          finalContract.carInfoFourWheelDrive != null && finalContract.carInfoFourWheelDrive !== ""
                            ? t(
                                finalContract.carInfoFourWheelDrive.toString().toLowerCase() === "true"
                                  ? "buyer.fourWheelDriveYes"
                                  : "buyer.fourWheelDriveNo",
                              )
                            : null,
                        ],
                        ["electricVehicleConfiguration", finalContract.carInfoElectricVehicleConfiguration],
                        ["firstInTraffic", finalContract.carInfoRegistrationDate],
                        ["previousOwners", finalContract.carInfoNumberOfUsers],
                        ["inspectionLast", finalContract.carInfoInspectionDate],
                        ["inspectionNext", finalContract.carInfoInspectionDateEMPTY],
                        ["previousInspectionDate", finalContract.carInfoPreviousInspectionDate],
                        ["dispensationInspectionGroup", finalContract.carInfoDispensationInspectionGroup],
                        ["inspectionGroup", finalContract.carInfoInspectionGroup],
                        ["inspectionStation", finalContract.carInfoInspectionStation],
                        ["inspectionProgramCode", finalContract.carInfoInspectionProgramCode],
                        ["previousInspectionProgramCode", finalContract.carInfoPreviousInspectionProgramCode],
                        ["odometer", finalContract.odometerKm ?? finalContract.carInfoFeedingStall],
                        ["keysCount", finalContract.keysCount],
                        ["tax", finalContract.carInfoTax],
                        ["malus", finalContract.carInfoMalus],
                      ].map(([key, val]) => (
                        <RegisterRow key={`${finalContract.id}:${key}`} label={t(`buyer.${key}`)} wide={key === "odometer" || key === "keysCount"}>
                          {key === "odometer" || key === "keysCount" ? (
                            <ContractEditableVehicleNumber
                              t={t}
                              kind={key}
                              value={val}
                              // "Change" tells the buyer this field is being edited (their page is
                              // held until it is saved or cancelled), like the price dialog does
                              onEditStart={() => void notifyBuyerPrice(FIELD_LOCK_MSG[key].changing)}
                              onEditCancel={() => void notifyBuyerPrice(FIELD_LOCK_MSG[key].cancelled)}
                              onValueChange={(nextValue) => {
                                if (key === "odometer") {
                                  // a new reading has to be certified afresh (the server clears it too)
                                  setOdometerCertified(false);
                                  setOdometerCertifiedAt(null);
                                  setOdometerEpoch((n) => n + 1);
                                }
                                void (async () => {
                                  await saveContractDetails(finalContract.id, key === "odometer" ? { odometerKm: nextValue } : { keysCount: nextValue });
                                  await notifyBuyerContractUi(key, nextValue);
                                })();
                              }}
                            />
                          ) : val || <span className="ds-doc-value--absent">{t("agreement.notSet")}</span>}
                        </RegisterRow>
                      ))}
                      <RegisterRow label={t("seller.car.steps.valuation")}>
                        {finalContract.carValuationBySeller
                          ? finalContract.carValuationBySeller.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                          : "-"}
                      </RegisterRow>
                      <RegisterRow label={t("buyer.equipmentIncluded")} wide>
                        <div className="ds-doc-field-wrap" data-saved={equipmentSaved ? "true" : undefined}>
                          <textarea
                            className="ds-doc-field"
                            aria-label={t("buyer.equipmentIncluded")}
                            value={equipmentText}
                            rows={Math.min(8, Math.max(2, equipmentText.split("\n").length))}
                            placeholder={t("agreement.equipmentPlaceholder")}
                            onChange={(e) => setEquipmentText(e.target.value)}
                            onBlur={handleEquipmentBlur}
                          />
                          <span className="ds-doc-field-saved" aria-live="polite">
                            {equipmentSaved ? t("agreement.saved") : ""}
                          </span>
                        </div>
                      </RegisterRow>
                    </Register>
                    <ContractOdometerAttestation
                      key={`${finalContract.id}:${finalContract.carInfoFeedingStall}:${sellerDisplayName}:${odometerEpoch}`}
                      t={t}
                      sellerName={sellerDisplayName}
                      canAct
                      initialCertifiedAt={odometerCertifiedAt === undefined ? finalContract.odometerCertifiedAt : odometerCertifiedAt}
                      onChange={(certified, at) => {
                        setOdometerCertified(certified);
                        setOdometerCertifiedAt(certified ? at : null);
                        void saveContractDetails(finalContract.id, { odometerCertified: certified });
                        // deal log: the seller certified the odometer reading
                        if (certified) logDealEvent(lastId, "odometer_certified");
                      }}
                    />
                    <ContractVehicleSourceNote t={t} />
                  </Clause>

                  <Clause id="section-parties" number={2} title={t("agreement.partiesTitle")}>
                    <div className="ds-doc-parties">
                      <Party t={t} roleKey="seller" name={sellerDisplayName} profile={sellerProfile} />
                      <Party t={t} roleKey="buyer" name={buyerUserName} profile={buyerProfile} />
                    </div>
                  </Clause>

                  <Clause id="section-price" number={3} title={t("contractUi.purchasePriceTitle")}>
                    {contractPriceEditing ? (
                      <form className="ds-doc-editing ds-doc-editor ds-contract-inline-price" onSubmit={saveContractPrice}>
                        <label className="ds-label" htmlFor="contract-purchase-price">{t("seller.priceLabel")}</label>
                        <input id="contract-purchase-price" className="ds-input ds-doc-input-short" type="number" min="0" inputMode="numeric" autoFocus value={contractPriceDraft} onChange={(event) => setContractPriceDraft(event.target.value)} disabled={contractPriceSaving} />
                        <p className="ds-contract-inline-price-error" role={contractPriceError ? "alert" : undefined}>{contractPriceError}</p>
                        <div className="ds-doc-edit-actions">
                          <button type="submit" className="ds-action-ink" disabled={contractPriceSaving}>{t("contractUi.save")}</button>
                          <button type="button" className="ds-doc-edit" onClick={cancelContractPriceEditor} disabled={contractPriceSaving}>{t("actions.cancel")}</button>
                        </div>
                      </form>
                    ) : (
                      <div className="ds-doc-price-line ds-doc-price-line--editable">
                        <p className="ds-doc-price">
                          {finalContract.carValuationBySeller
                            ? formatSekAmount(finalContract.carValuationBySeller)
                            : "-"}
                        </p>
                        <button type="button" className="ds-doc-edit" onClick={openContractPriceEditor} disabled={loading}>{t("contractUi.edit")}</button>
                      </div>
                    )}
                  </Clause>

                  <Clause id="section-delivery" number={4} title={t("contractUi.deliveryTitle")}>
                    <Terms clause={4} items={[t("contractUi.deliveryRiskText")]} />
                  </Clause>

                  <Clause id="section-condition" number={5} title={t("seller.conditionDefects.title")}>
                    <Terms
                      clause={5}
                      items={[t("contractUi.conditionAsIs"), t("contractUi.conditionClaim"), t("contractUi.conditionNotify")]}
                    />
                    <Register className="mt-4">
                      <RegisterRow label={t("buyer.knownDefects")} wide>
                        <div className="ds-doc-field-wrap" data-saved={knownDefectsSaved ? "true" : undefined}>
                          <textarea className="ds-doc-field" aria-label={t("buyer.knownDefects")} value={knownDefectsText} rows={Math.min(8, Math.max(3, knownDefectsText.split("\n").length))} placeholder={t("contractUi.knownDefectsPlaceholder")} onChange={(event) => setKnownDefectsText(event.target.value)} onBlur={handleKnownDefectsBlur} />
                          <span className="ds-doc-field-saved" aria-live="polite">{knownDefectsSaved ? t("agreement.saved") : ""}</span>
                        </div>
                      </RegisterRow>
                    </Register>
                  </Clause>

                  <Clause id="section-debt" number={6} title={t("contractUi.debtsLoansTitle")}>
                    <Register>
                      <ContractOptionRow t={t} label={t("seller.debts.title")} name="debtOption" value={debtOption} onChange={(nextOption) => { setDebtOption(nextOption); void persistContractTerms(nextOption, value); void notifyBuyerContractUi("debt", { option: nextOption }); }}
                        options={["none", "seller"].map((opt) => ({ value: opt, label: t(`seller.debts.options.${opt}`) }))} />
                      <ContractOptionRow t={t} label={t("contractUi.loanTitle")} name="loanLienOption" value={value} onChange={(nextOption) => { setValue(nextOption); void persistContractTerms(debtOption, nextOption); void notifyBuyerContractUi("loan", { option: nextOption }); }}
                        options={["none", "seller"].map((opt) => ({ value: opt, label: t(`seller.loan.options.${opt}`) }))} />
                    </Register>
                    <p className="ds-doc-source">{t("contractUi.debtsLoansSource")}</p>
                  </Clause>

                  <Clause id="section-ownership" number={7} title={t("contractUi.ownershipTitle")}>
                    <Terms
                      clause={7}
                      items={[t("contractUi.ownershipRetention"), t("contractUi.ownershipDocuments"), t("contractUi.ownershipDrivingBan")]}
                    />
                  </Clause>

                  <Clause id="section-transport" number={8} title={t("contractUi.transportTitle")}>
                    <Terms clause={8} items={[t("contractUi.transportCommitment"), t("contractUi.transportNote")]} />
                  </Clause>

                  <ContractSignatureClause
                    t={t}
                    role="seller"
                    contract={finalContract}
                    sellerName={sellerDisplayName}
                    buyerName={buyerUserName}
                    loading={loading}
                    onConfirm={handleSellerDealConfirm}
                    canConfirm={odometerCertified}
                    remaining={
                      !odometerCertified ? [{ label: t("contractUi.remainingOdometer"), targetId: "seller-odometer-block" }] : []
                    }
                    signedAt={{ seller: sellerSignedAt, buyer: buyerSignedAt }}
                    signing={sellerSignModalOpen || loading}
                    onCancelSigning={() => setSellerSignModalOpen(false)}
                  />
                </article>
              )}
            </div>
          )}

          <BankIdSignModal
            presentation="contract"
            signData={{
              contractId: lastId,
              role: "seller",
              reg: carRegNo || registration,
              carName: [
                (finalContract || contractData)?.carInfoVehicleDesignation,
                (finalContract || contractData)?.carInfoTradeName,
              ].filter(Boolean).join(" "),
              price: priceValue,
              sellerName: sellerDisplayName,
              buyerName: buyerUserName,
              version:
                (finalContract || contractData)?.lastModificationTime ||
                (finalContract || contractData)?.creationTime,
            }}
            open={sellerSignModalOpen}
            onClose={() => setSellerSignModalOpen(false)}
            onSuccess={async (_pn, orderRef) => {
              setSellerSignModalOpen(false);
              await executeSellerConfirm(orderRef);
            }}
          />
        </main>
      </div>
    </PublicPage>
  );
}

export default SellerProfile;
